import React, { useState, useEffect, useRef, useMemo } from 'react';
import ErrorBoundary from '../../../../components/Common/ErrorBoundary';

// ─── constants ───────────────────────────────────────────────────────────────

const KNOWN_META = new Set(['SiteName', 'Name', 'LastBackup', 'LastBackupAgo']);
const PAGE_SIZE  = 10;

function extractHourKeys(rows = []) {
  if (!rows.length) return [];
  return Object.keys(rows[0]).filter(k => !KNOWN_META.has(k));
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const statusColor = (status) => {
  if (status === 0) return '#f44336';
  if (status === 1) return '#ff9800';
  if (status === 2) return '#4caf50';
  return null; // handled by CSS class
};

const statusLabel = (status) => {
  if (status === null || status === undefined) return 'No data';
  if (status === 0) return 'Not Backed Up';
  if (status === 1) return 'Partial';
  if (status === 2) return 'Backed Up';
  return 'Unknown';
};

function fmtDisplay(date) {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmtYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ─── theme helper — returns `vbt-dark__<name>` or `vbt-light__<name>` ────────
const tc = (isDark, name) => isDark ? `vbt-dark__${name}` : `vbt-light__${name}`;

// ─── Calendar picker ─────────────────────────────────────────────────────────

const CAL_DAYS   = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const CAL_MONTHS = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December'];

function CalendarPicker({ selectedDate, onChange, onClose, isDarkTheme }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [vy, setVy] = useState(selectedDate.getFullYear());
  const [vm, setVm] = useState(selectedDate.getMonth());

  const firstDay    = new Date(vy, vm, 1).getDay();
  const daysInMonth = new Date(vy, vm + 1, 0).getDate();
  const atCurrent   = vy === today.getFullYear() && vm === today.getMonth();

  const prevM = () => { if (vm === 0) { setVm(11); setVy(y => y-1); } else setVm(m => m-1); };
  const nextM = () => {
    if (atCurrent) return;
    if (vm === 11) { setVm(0); setVy(y => y+1); } else setVm(m => m+1);
  };

  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const T = (name) => tc(isDarkTheme, name);

  return (
    <div className={T('cal-popover')}>
      <div className={T('cal-nav')}>
        <button onClick={prevM} className={T('cal-nav-btn')}>‹</button>
        <span className={T('cal-nav-title')}>{CAL_MONTHS[vm]} {vy}</span>
        <button onClick={nextM} disabled={atCurrent}
          className={`${T('cal-nav-btn')} ${atCurrent ? T('cal-nav-btn--disabled') : ''}`}>›</button>
      </div>

      <div className={T('cal-dow-grid')}>
        {CAL_DAYS.map(d => <div key={d} className={T('cal-dow-cell')}>{d}</div>)}
      </div>

      <div className={T('cal-day-grid')}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e${idx}`} />;
          const cell = new Date(vy, vm, day);
          cell.setHours(0, 0, 0, 0);
          const isFuture   = cell > today;
          const isSelected = selectedDate.getFullYear() === vy && selectedDate.getMonth() === vm && selectedDate.getDate() === day;
          const isToday    = cell.getTime() === today.getTime();

          let cls = T('cal-day-btn');
          if (isSelected)    cls += ` ${T('cal-day-btn--selected')}`;
          else if (isToday)  cls += ` ${T('cal-day-btn--today')}`;
          else if (isFuture) cls += ` ${T('cal-day-btn--future')}`;

          return (
            <button key={day} disabled={isFuture} onClick={() => { onChange(cell); onClose(); }} className={cls}>
              {day}
            </button>
          );
        })}
      </div>

      <div className={T('cal-today-row')}>
        <button onClick={() => { onChange(new Date(today)); onClose(); }} className={T('cal-today-btn')}>
          Today
        </button>
      </div>
    </div>
  );
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────

function Tooltip({ text, children, isDarkTheme }) {
  const [vis, setVis] = useState(false);
  return (
    <div className={tc(isDarkTheme, 'tooltip-wrap')}
      onMouseEnter={() => setVis(true)} onMouseLeave={() => setVis(false)}>
      {children}
      {vis && <div className={tc(isDarkTheme, 'tooltip-box')}>{text}</div>}
    </div>
  );
}

// ─── Layout constants ─────────────────────────────────────────────────────────

const NAME_COL = 210;
const CELL_W   = 30;

// ─── Hour header ─────────────────────────────────────────────────────────────

function HourHeader({ hourKeys, isDarkTheme }) {
  const T = (name) => tc(isDarkTheme, name);
  return (
    <div className={T('hour-header')}>
      <div className={T('hour-header-label')}>CAMERA SOURCE</div>
      <div className={T('hour-header-cells')}>
        {hourKeys.map(h => (
          <div key={h} className={T('hour-header-cell')} style={{ width: CELL_W }}>{h}</div>
        ))}
      </div>
    </div>
  );
}

// ─── Device row ───────────────────────────────────────────────────────────────

function DeviceRow({ row, hourKeys, dateStr, isDarkTheme }) {
  const T = (name) => tc(isDarkTheme, name);
  return (
    <div className={T('device-row')}>
      <div className={T('device-meta')} style={{ width: NAME_COL }}>
        <div className={T('device-name')}>{row.Name}</div>
        <div className={T('device-site')}>{row.SiteName}</div>
        <div className={T('device-last')}>
          {row.LastBackup ? `Last: ${row.LastBackup}` : (row.LastBackupAgo ?? 'Never')}
        </div>
      </div>
      <div className={T('cells-row')}>
        {hourKeys.map(h => {
          const status = row[h] ?? null;
          const color  = statusColor(status);
          return (
            <Tooltip key={h} text={`${dateStr} ${h} — ${statusLabel(status)}`} isDarkTheme={isDarkTheme}>
              <div
                className={`${T('cell')}${!color ? ` ${T('cell--nodata')}` : ''}`}
                style={{ width: CELL_W, height: CELL_W, ...(color ? { background: color } : {}) }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.6'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              />
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function PaginationBar({ page, totalPages, onChange, total, from, to, isDarkTheme }) {
  if (totalPages <= 1) return null;
  const T = (name) => tc(isDarkTheme, name);

  const show   = new Set([1, totalPages, page, page-1, page+1].filter(p => p >= 1 && p <= totalPages));
  const sorted = [...show].sort((a, b) => a - b);
  const pages  = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i-1] > 1) pages.push('…');
    pages.push(sorted[i]);
  }

  return (
    <div className={T('pagination')}>
      <span className={T('pagination-info')}>Showing {from}–{to} of {total} devices</span>
      <div className={T('pagination-btns')}>
        <button onClick={() => onChange(page-1)} disabled={page === 1}
          className={`${T('pg-btn')} ${page === 1 ? T('pg-btn--disabled') : ''}`}>‹</button>

        {pages.map((p, i) =>
          p === '…'
            ? <span key={`el${i}`} className={T('pg-ellipsis')}>…</span>
            : <button key={p} onClick={() => onChange(p)}
                className={`${T('pg-btn')} ${p === page ? T('pg-btn--active') : ''}`}>{p}</button>
        )}

        <button onClick={() => onChange(page+1)} disabled={page === totalPages}
          className={`${T('pg-btn')} ${page === totalPages ? T('pg-btn--disabled') : ''}`}>›</button>
      </div>
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend({ isDarkTheme }) {
  const T = (name) => tc(isDarkTheme, name);
  const items = [
    { color: '#f44336', label: 'Not Backed Up (0%-50%)' },
    { color: '#ff9800', label: 'Partial (50%-90%)' },
    { color: '#4caf50', label: 'Backed Up (90% - 100%)' },
  ];
  return (
    <div className={T('legend')}>
      {items.map(({ color, label }) => (
        <div key={label} className={T('legend-item')}>
          <div className={T('legend-dot')} style={{ background: color }} />
          <span className={T('legend-label')}>{label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const BackupActivityTimeline = ({
  widgetConfig = {},
  widgetData,
  apiData,
  fetchData,
  selectedDate,
  setSelectedDate,
  isDarkTheme,
}) => {
  const { WidgetName = 'BackUP Activity Timeline', WidgetColor = '#1e1e3a', WidgetID = '' } = widgetConfig || {};
  const T = (name) => tc(isDarkTheme, name);

  // ── calendar ─────────────────────────────────────────────────────────────
  const [calOpen, setCalOpen] = useState(false);
  const calRef                = useRef(null);

  useEffect(() => {
    const h = (e) => { if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── parse apiData ─────────────────────────────────────────────────────────
  const allRows = useMemo(() => {
    if (!apiData) return [];
    if (Array.isArray(apiData?.Data?.[0]?.WidgetData)) return apiData.Data[0].WidgetData;
    if (Array.isArray(apiData?.WidgetData))            return apiData.WidgetData;
    if (Array.isArray(apiData))                        return apiData;
    return [];
  }, [apiData]);

  const hourKeys = useMemo(() => extractHourKeys(allRows), [allRows]);

  // ── pagination ────────────────────────────────────────────────────────────
  const [page, setPage]   = useState(1);
  const prevDateStrRef    = useRef(selectedDate ? fmtYMD(selectedDate) : '');

  useEffect(() => {
    const ds = selectedDate ? fmtYMD(selectedDate) : '';
    if (ds !== prevDateStrRef.current) { setPage(1); prevDateStrRef.current = ds; }
  }, [selectedDate]);

  const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageStart  = (safePage - 1) * PAGE_SIZE;
  const pageRows   = allRows.slice(pageStart, pageStart + PAGE_SIZE);
  const dateStr    = selectedDate ? fmtYMD(selectedDate) : '';

  return (
    <ErrorBoundary>
      <div className={T('widget')} key={`widget-${WidgetID}`}>

        {/* Header */}
        <div className={T('widget-header')} style={{ backgroundColor: WidgetColor }}>
          <span className={T('widget-title')}>{WidgetName}</span>
          <div ref={calRef} className={T('datepicker-wrap')}>
            <button onClick={() => setCalOpen(o => !o)} className={T('datepicker-btn')}>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="2" width="12" height="11" rx="2" stroke="#fff" strokeWidth="1.2"/>
                <path d="M1 5h12" stroke="#fff" strokeWidth="1.2"/>
                <path d="M4 1v2M10 1v2" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              {selectedDate ? fmtDisplay(selectedDate) : '—'}
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
            {calOpen && selectedDate && (
              <CalendarPicker
                selectedDate={selectedDate}
                onChange={d => { setSelectedDate(d); setCalOpen(false); }}
                onClose={() => setCalOpen(false)}
                isDarkTheme={isDarkTheme}
              />
            )}
          </div>
        </div>

        {/* Scrollable body — pagination + legend are OUTSIDE so always visible */}
        <div className={T('widget-body')}>
          {allRows.length === 0 ? (
            <div className={T('empty')}>
              No backup data found{selectedDate ? ` for ${fmtDisplay(selectedDate)}` : ''}.
            </div>
          ) : (
            <div className={T('scroll-x')}>
              <div style={{ minWidth: NAME_COL + hourKeys.length * (CELL_W + 3) + 36 }}>
                <HourHeader hourKeys={hourKeys} isDarkTheme={isDarkTheme} />
                {pageRows.map((row, idx) => (
                  <DeviceRow
                    key={`${row.Name}-${pageStart + idx}`}
                    row={row}
                    hourKeys={hourKeys}
                    dateStr={dateStr}
                    isDarkTheme={isDarkTheme}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Always visible — outside scrollable body */}
        <PaginationBar
          page={safePage} totalPages={totalPages} onChange={setPage}
          total={allRows.length} from={pageStart + 1}
          to={Math.min(pageStart + PAGE_SIZE, allRows.length)}
          isDarkTheme={isDarkTheme}
        />
        <Legend isDarkTheme={isDarkTheme} />

      </div>
    </ErrorBoundary>
  );
};

export default BackupActivityTimeline;